import {BarTreeGenerator, biasedRandomInt, flatten, RhythmEvent} from "@measure-up/rhythm";
import {NoteSource} from "./note-source";
import {MidiNote} from "./note-selection";
import type {MelodyEvent} from "./phrase-cycle";

/**
 * Bias of the per-phrase target candidate count draw towards maxNoteCount,
 * in percent. 75 weights towards longer phrases while still varying the
 * count; the draw concentrates around 75% of the range, so very short
 * phrases only occur at small caps. Hardcoded for now; a candidate control
 * later.
 */
const NOTE_COUNT_BIAS = 75;

/**
 * Bias of the per-group absorb draw towards absorbing all followers, in
 * percent. 90 weights towards full-length notes so rests are occasional,
 * not the norm. Hardcoded for now; a candidate control later.
 */
const ABSORB_BIAS = 70;

/** A sounding note in a phrase: duration in beats, pitch as a MIDI note. */
interface NoteEvent {
    kind: 'note';
    /** Duration in beats, in the same unit flatten() produced. */
    duration: number;
    pitch: MidiNote;
}

interface PhraseGeneratorConfig {
    beatsPerBar: number;
    /** 1..100, as BarTreeGenerator expects. */
    granularity: number;
    /** 1..100, as BarTreeGenerator expects. */
    tupletBias: number;
    /** Cap on sounding notes per phrase; clamped to >= 1 at generate() time. */
    maxNoteCount: number;
}

/**
 * PhraseGenerator - produces one phrase bar of MelodyEvents (notes and
 * rests interleaved) on demand.
 *
 * Pipeline per generate() call (ADR 0002 as amended by ADR 0004):
 *
 *   BarTreeGenerator -> flatten (the onset template: one bar of slots)
 *     -> draw a target candidate count in [1, maxNoteCount] (biased towards
 *        maxNoteCount) -> select `target` slots uniformly (candidates)
 *     -> per group (candidate + following unselected slots): draw
 *        K = biasedRandomInt(0, followers, ABSORB_BIAS), absorb the first K
 *        followers into the candidate's duration, emit the remaining
 *        followers as RestEvents
 *     -> one NoteSource.next() per SELECTED slot, in order
 *
 * The config object is held by reference and read on every generate(), so
 * control changes apply to the next phrase - the same live-read pattern as
 * BarTreeGenerator's config and NoteSource's selection. The internal
 * BarTreeGenerator is built with minLeafDepth 0 hardcoded (allowing half
 * notes is Phraser's identity, per ADR 0002), sharing the config's
 * beatsPerBar/granularity/tupletBias by reference so they stay live all
 * the way down.
 *
 * Only selected slots receive pitches; unselected slots are rests and
 * never draw from the NoteSource (preserving the seam for future pitch
 * weighting). NoteSource throws on an empty sounded set; that error is
 * deliberately not caught here (the UI disables play in that state).
 *
 * Slot selection is uniform with no positional weighting: the ADR 0002
 * downbeat guarantee is removed by construction (ADR 0004), so a phrase
 * can start late. The selection/absorb logic lives here, not in the
 * rhythm kernel: articulation is phraser semantics with a single consumer.
 */
class PhraseGenerator {

    private readonly config: PhraseGeneratorConfig;
    private readonly noteSource: NoteSource;
    private readonly barTreeGenerator: BarTreeGenerator;

    constructor(config: PhraseGeneratorConfig, noteSource: NoteSource) {
        this.config = config;
        this.noteSource = noteSource;
        // a live view over the caller's config: getters forward the three
        // shared fields so mutations reach the bar generator, without
        // mutating the caller's object (which Object.assign would) or
        // copying it (which a spread would, breaking liveness)
        this.barTreeGenerator = new BarTreeGenerator({
            get beatsPerBar() { return config.beatsPerBar; },
            get granularity() { return config.granularity; },
            get tupletBias() { return config.tupletBias; },
            minLeafDepth: 0,
        });
    }

    /** Generate one phrase bar: notes and rests interleaved, tiling the bar exactly. */
    generate(): MelodyEvent[] {
        const maxNoteCount = Math.max(1, this.config.maxNoteCount);
        // a fresh target per phrase: at most the cap, usually close to it
        const target = biasedRandomInt(1, maxNoteCount, NOTE_COUNT_BIAS);
        const slots = flatten(this.barTreeGenerator.generate(), this.config.beatsPerBar);
        const selected = this.selectSlots(slots.length, target);
        return this.articulate(slots, selected);
    }

    /**
     * Uniform pick of `target` distinct slot indices (partial Fisher-Yates).
     * No positional weighting: slot 0 is as likely to be unselected as any
     * other. Under the target (the rhythm provides fewer slots) every slot
     * is selected - no padding, fewer notes than the target is fine.
     */
    private selectSlots(slotCount: number, target: number): Set<number> {
        const pickCount = Math.min(target, slotCount);
        const pool = Array.from({length: slotCount}, (_, i) => i);
        for (let i = 0; i < pickCount; i++) {
            const j = i + Math.floor(Math.random() * (slotCount - i));
            [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        return new Set(pool.slice(0, pickCount));
    }

    /**
     * Walk the template once. Each selected slot starts a group (the
     * candidate plus the following unselected slots up to the next
     * candidate or the bar end); the group draws
     * K = biasedRandomInt(0, followers, ABSORB_BIAS), absorbs the first K
     * followers' durations into the candidate's note, and emits the
     * remaining followers as rests. Unselected slots with no candidate
     * before them emit leading rests. Absorbed slots always form a prefix
     * of their group and rests a suffix, so a note never spans a gap.
     * Absorption conserves durations exactly; the emitted events tile the
     * bar. Pitches are zipped in here, one next() per candidate, in order.
     */
    private articulate(slots: RhythmEvent[], selected: Set<number>): MelodyEvent[] {
        const events: MelodyEvent[] = [];
        let i = 0;
        while (i < slots.length) {
            if (!selected.has(i)) {
                // unselected with no candidate before it: a leading rest
                events.push({kind: 'rest', duration: slots[i].duration});
                i++;
                continue;
            }
            let nextCandidate = i + 1;
            while (nextCandidate < slots.length && !selected.has(nextCandidate)) {
                nextCandidate++;
            }
            const followers = nextCandidate - i - 1;
            const absorbed = biasedRandomInt(0, followers, ABSORB_BIAS);
            let duration = slots[i].duration;
            for (let f = 0; f < absorbed; f++) {
                duration += slots[i + 1 + f].duration;
            }
            events.push({kind: 'note', duration, pitch: this.noteSource.next()});
            for (let f = absorbed; f < followers; f++) {
                events.push({kind: 'rest', duration: slots[i + 1 + f].duration});
            }
            i = nextCandidate;
        }
        return events;
    }
}

export {PhraseGenerator, PhraseGeneratorConfig, NoteEvent};
